import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ReleaseNotesService } from '../../services/master';
import { ToastrService } from 'ngx-toastr';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { WhatsDeleteModalComponent } from '../whats-delete-modal/whats-delete-modal.component';
import { TranslateService } from '@ngx-translate/core';
import { HasPermissionService } from 'src/app/services/common/has-permission.service';

@Component({
  standalone: false,
  selector: 'app-whats-new-edit',
  templateUrl: './whats-new-edit.component.html',
  styleUrls: ['./whats-new-edit.component.scss']
})
export class WhatsNewEditComponent implements OnInit {
  releaseNoteForm: FormGroup;
  releaseNotesID: number;
  today = new Date().toISOString().split('T')[0];

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private releseNoteService: ReleaseNotesService,
    private toastr: ToastrService,
    private modalService: NgbModal,
    private translate: TranslateService,
    private permission: HasPermissionService,
  ) {}

  ngOnInit(): void {
    this.initForm();

    this.route.queryParams.subscribe(params => {
      const encoded = params['data'];
      if (!encoded) {
        this.router.navigate(['/whats-new-list']);
        return;
      }

      try {
        const decoded = decodeURIComponent(escape(atob(encoded)));
        const parsedData = JSON.parse(decoded);
        this.populateForm(parsedData);
      } catch {
        this.router.navigate(['/whats-new-list']);
      }
    });
  }

  initForm(): void {
    this.releaseNoteForm = this.fb.group({
      releaseNoteLabel: ['', Validators.required],
      initialDate: ['', Validators.required],
      endDate: ['', Validators.required],
      isActive: [true],
      releaseNotes: this.fb.array([])
    });
  }

  get releaseNotes(): FormArray {
    return this.releaseNoteForm.get('releaseNotes') as FormArray;
  }

  private buildNote(text: string = '', isHeading: boolean = false) {
    return this.fb.group({
      text: [text, Validators.required],
      isHeading: [isHeading]
    });
  }

  addNoteField(text: string = '', isHeading: boolean = false) {
    this.releaseNotes.push(this.buildNote(text, isHeading));
  }

  removeNoteField(index: number) {
    this.releaseNotes.removeAt(index);
  }

  populateForm(data: any): void {
    this.releaseNotesID = data.releaseNotesID;

    const formattedInitialDate = this.convertToISO(data.initialDate);
    const formattedEndDate = data.endDate?.split('T')[0] || '';

    this.releaseNoteForm.patchValue({
      releaseNoteLabel: data.releaseNoteLabel,
      initialDate: formattedInitialDate,
      endDate: formattedEndDate,
      isActive: data.isActive
    });

    const notesArray = Array.isArray(data.releaseNote) ? data.releaseNote : [];

    if (notesArray.length > 0) {
      notesArray.forEach((n: any) => {
        if (n && typeof n === 'object' && 'text' in n) {
          this.addNoteField(String(n.text ?? ''), !!n.isHeading);
        } else if (typeof n === 'string') {
          const m = n.match(/^<strong>(.*)<\/strong>$/i);
          if (m) this.addNoteField(m[1], true);
          else this.addNoteField(n, false);
        }
      });
    } else {
      this.addNoteField();
    }
  }

  convertToISO(dateStr: string): string {
    const parsed = new Date(dateStr);
    if (isNaN(parsed.getTime())) return '';
    return parsed.toISOString().split('T')[0];
  }

  back(): void {
    this.router.navigate(['/whats-new-list']);
  }

  save(): void {
    if (this.releaseNoteForm.invalid) return;

    const formValue = this.releaseNoteForm.value;
    const notesAsStrings: string[] = (formValue.releaseNotes || []).map((n: any) => {
      const txt = String(n?.text ?? '');
      return n?.isHeading ? `<strong>${txt}</strong>` : txt;
    });

    const payload = {
      releaseNotesID: this.releaseNotesID,
      releaseNoteLabel: formValue.releaseNoteLabel,
      initialDate: formValue.initialDate,
      endDate: formValue.endDate,
      isActive: formValue.isActive,
      releaseNote: JSON.stringify({ ReleaseNotes: notesAsStrings })
    };

    this.releseNoteService.apiReleaseNotesUpdateReleaseNotesPost(payload).subscribe({
      next: () => {
        this.toastr.success(this.translate.instant('Action.Success'));
        this.router.navigate(['/whats-new-list']);
      },
      error: () => {
        this.toastr.error(this.translate.instant('Action.Error'));
      }
    });
  }

  exportJson(): void {
    const formValue = this.releaseNoteForm.value;
    const exportPayload = {
      releaseNoteLabel: formValue.releaseNoteLabel ?? '',
      initialDate: formValue.initialDate ?? '',
      endDate: formValue.endDate ?? '',
      isActive: !!formValue.isActive,
      releaseNote: JSON.stringify({
        ReleaseNotes: (formValue.releaseNotes || []).map((n: any) => ({
          text: String(n?.text ?? ''),
          isHeading: !!n?.isHeading
        }))
      })
    };

    const blob = new Blob([JSON.stringify(exportPayload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const safeLabel = (exportPayload.releaseNoteLabel || 'release-notes').replace(/[^a-z0-9-_]+/gi, '_');
    a.href = url;
    a.download = `${safeLabel}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  onImportFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const raw = String(reader.result || '');
        const parsed = JSON.parse(raw);

        this.releaseNoteForm.patchValue({
          releaseNoteLabel: parsed.releaseNoteLabel ?? this.releaseNoteForm.get('releaseNoteLabel')?.value ?? '',
          initialDate: parsed.initialDate ?? this.releaseNoteForm.get('initialDate')?.value ?? '',
          endDate: parsed.endDate ?? this.releaseNoteForm.get('endDate')?.value ?? '',
          isActive: typeof parsed.isActive === 'boolean'
            ? parsed.isActive
            : !!this.releaseNoteForm.get('isActive')?.value
        });

        let rnPayload: any = parsed.releaseNote;
        if (typeof rnPayload === 'string') {
          try { rnPayload = JSON.parse(rnPayload); } catch { rnPayload = null; }
        }
        const arr = Array.isArray(rnPayload?.ReleaseNotes) ? rnPayload.ReleaseNotes : [];

        while (this.releaseNotes.length) this.releaseNotes.removeAt(0);

        if (arr.length) {
          arr.forEach((item: any) => {
            if (item && typeof item === 'object' && 'text' in item) {
              this.addNoteField(String(item.text ?? ''), !!item.isHeading);
            } else if (typeof item === 'string') {
              const m = item.match(/^<strong>(.*)<\/strong>$/i);
              if (m) this.addNoteField(m[1], true);
              else this.addNoteField(item, false);
            }
          });
        } else {
          this.addNoteField();
        }

        this.toastr.success(this.translate.instant('Action.Success'));
      } catch {
        this.toastr.error(this.translate.instant('Action.Error'));
      } finally {
        input.value = '';
      }
    };
    reader.readAsText(file);
  }

  openDeleteModal(): void {
    const modalRef = this.modalService.open(WhatsDeleteModalComponent);
    modalRef.componentInstance.releaseNotesID = this.releaseNotesID;

    modalRef.result.then((confirmed) => {
      if (confirmed) {
        this.releseNoteService.apiReleaseNotesDeleteReleaseNotesIdDelete(this.releaseNotesID).subscribe({
          next: () => {
            this.toastr.success(this.translate.instant('Action.Success'));
            this.router.navigate(['/whats-new-list']);
          },
          error: () => {
            this.toastr.error(this.translate.instant('Action.Error'));
          }
        });
      }
    }).catch(() => {});
  }
}